import { AppSettings } from '@/Infrastructure/Configuration/types';
import { useAppSelector } from '@reduxStore';
import { useCallback, useState } from 'react';
import useEntraAccount from './useEntraAccount';
import { getApiErrorMessage } from '@api/getApiError';

/** Extract the filename for the report download from the Content-Disposition header */
function getFilenameFromContentDisposition(contentDisposition: string): string | undefined {
  console.log('Content-Disposition header:', contentDisposition);
  // Prefer RFC 5987 filename* (e.g. filename*=UTF-8''My%20File.xlsx)
  const extendedMatch = /filename\*\s*=\s*(?:[^']*)'(?:[^']*)'([^;]+)/i.exec(contentDisposition);
  if (extendedMatch?.[1]) {
    try {
      return decodeURIComponent(extendedMatch[1].trim());
    } catch {
      // fall through to standard filename
    }
  }

  const match = /filename\s*=\s*("([^"]+)"|([^;]+))/i.exec(contentDisposition);
  const raw = match?.[2] ?? match?.[3];
  return raw?.trim() || undefined;
}

/** Extract the filename for the report download from the URL */
function getFilenameFromUrl(url: string): string | undefined {
  const urlParts = url.split('/');
  const rawFilename = urlParts[urlParts.length - 1];
  return rawFilename ? rawFilename.split('?')[0] || undefined : undefined;
}

export default function useFetch() {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { getAccessToken } = useEntraAccount();
  const { triniumApiBaseUrl } = useAppSelector((state) => state.appSettings as AppSettings);

  const fetchFile = useCallback(
    async (url: string, method?: string, body?: string, accept?: string) => {
      setIsLoading(true);

      const token = await getAccessToken();
      const separator = !url.startsWith('/') ? '/' : '';
      const concatUrl = `${triniumApiBaseUrl}${separator}${url}`;

      const request = fetch(concatUrl, {
        method: method,
        body: body,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: accept ?? 'application/json'
        }
      });

      await request
        .then(async (response) => {
          const contentType = response.headers.get('content-type');
          if (!contentType || contentType.includes('application/json')) {
            setIsError(true);
            setErrorMessage(getApiErrorMessage(await response.json()));
          } else {
            const blob = await response.blob();
            const target = URL.createObjectURL(blob);
            const contentTypeHeader = response.headers.get('content-type') || '';
            if (
              contentTypeHeader.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
              contentTypeHeader.includes('application/vnd.ms-excel')
            ) {
              const contentDisposition = response.headers.get('content-disposition') || '';
              const filename = getFilenameFromContentDisposition(contentDisposition) ?? getFilenameFromUrl(url) ?? 'download.xlsx';
              const link = document.createElement('a');
              link.href = target;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(target);
            } else {
              window.open(target, '_blank')?.focus();
            }
          }
        })
        .catch((error: unknown) => {
          setIsError(true);
          setErrorMessage(getApiErrorMessage(error));
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [getAccessToken, triniumApiBaseUrl]
  );

  return {
    /** Flag indicating if in a loading state */
    isLoading,
    /** Flag indicating if in an error state */
    isError,
    /** String representation of the deserialized error when in an error state */
    errorMessage,
    /** Wrapper around the fetch to download an API file and display in a new tab */
    fetchFile
  };
}
